import { ILogger } from './logger.interface';
import { NoopLogger } from './noop-logger';

export enum LogLevel {
  DEBUG = 0,
  INFO,
  WARN,
  ERROR,
}

const LOG_LEVEL_LABELS = {
  [LogLevel.DEBUG]: 'DEBUG',
  [LogLevel.INFO]: 'INFO',
  [LogLevel.WARN]: 'WARN',
  [LogLevel.ERROR]: 'ERROR',
};

export function getLogLevel(level: string): LogLevel | undefined {
  if (!level) return undefined;
  const normalizedLevel = level.toLowerCase();
  const knownLevels: Record<string, LogLevel> = {
    debug: LogLevel.DEBUG,
    info: LogLevel.INFO,
    warn: LogLevel.WARN,
    warning: LogLevel.WARN,
    error: LogLevel.ERROR,
  };

  return knownLevels[normalizedLevel];
}

interface ILogOutput {
  name?: string;
  traceId?: string;

  timestamp: string;
  level: string;
  message: string;
  misc: unknown[];
}

export class UtilityLogger {
  private minLevel: LogLevel;
  private name?: string;
  private traceId?: string;
  private logger: ILogger;

  private formatter!: (level: LogLevel, message: string, ...args: unknown[]) => string;

  constructor(minLevel: LogLevel, name?: string, traceId?: string) {
    this.minLevel = minLevel;
    this.name = name;
    this.traceId = traceId;
    this.logger = new NoopLogger();
    this.setFormatter();
  }

  private setFormatter(): void {
    // bind the name and traceId to the formatter
    this.formatter = (level: LogLevel, message: string, ...args: unknown[]) => {
      const output: ILogOutput = {
        name: this.name,
        traceId: this.traceId,
        timestamp: new Date().toISOString(),
        level: LOG_LEVEL_LABELS[level],
        message,
        misc: args,
      };

      // remove the name and traceId if they are not provided
      if (!this.name) {
        delete output.name;
      }
      if (!this.traceId) {
        delete output.traceId;
      }
      return JSON.stringify(output);
    };
  }

  setLogger(logger: ILogger): UtilityLogger {
    this.logger = logger;
    return this;
  }

  setMinLevel(minLevel: LogLevel): UtilityLogger {
    this.minLevel = minLevel;
    this.setFormatter();
    return this;
  }
  setName(name: string): UtilityLogger {
    this.name = name;
    this.setFormatter();
    return this;
  }
  setTraceId(traceId: string): UtilityLogger {
    this.traceId = traceId;
    this.setFormatter();
    return this;
  }

  debug(message: string, ...args: unknown[]): void {
    if (this.minLevel <= LogLevel.DEBUG) {
      this.logger.debug(this.formatter(LogLevel.DEBUG, message, ...args));
    }
  }

  // alias for info
  log(message: string, ...args: unknown[]): void {
    if (this.minLevel <= LogLevel.INFO) {
      this.logger.info(this.formatter(LogLevel.INFO, message, ...args));
    }
  }

  info(message: string, ...args: unknown[]): void {
    if (this.minLevel <= LogLevel.INFO) {
      this.logger.info(this.formatter(LogLevel.INFO, message, ...args));
    }
  }

  warn(message: string, ...args: unknown[]): void {
    if (this.minLevel <= LogLevel.WARN) {
      this.logger.warn(this.formatter(LogLevel.WARN, message, ...args));
    }
  }

  error(message: string, ...args: unknown[]): void {
    if (this.minLevel <= LogLevel.ERROR) {
      this.logger.error(this.formatter(LogLevel.ERROR, message, ...args));
    }
  }

  /**
   * this method is used to log to the console without checking the level
   * @param level - the level of the log
   * @param message - the message to log
   * @param args - the arguments to log
   */
  write(level: LogLevel, message: string, ...args: unknown[]): void {
    this.logger.log(this.formatter(level, message, ...args));
  }
}
